import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar } from '@mui/material';

import { USER_ROLES, fileTypes } from '../../constants';
import CustomButton from '../reusable/CustomButton';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import Loader from '../reusable/Loader';
import TableSkeleton from '../reusable/TableSkeleton';
import Panel from '../reusable/Panel';
import { getFocusGroupWorkflowStatus } from '../../services/focusGroupWorkflowService';
import { getConfiguredSchools, getEOAConfig } from '../../services/eoaConfigServices';
import { ConfiguredEOASchool } from '../../interfaces/EOAInterface';
import CustomConfirmationModal from '../reusable/CustomConfirmationModal';
import useUserDetails from '../hooks/useUserDetails';
import { getFinalizedReportUrl, getSchoolFacilitators, Facilitator } from '../../services/focusGroupService';
import { postRequestUploadUrl, uploadFileToS3Bucket } from '../../services/uploadService';
import FocusGroupReportStatus from './FocusGroupReportStatus';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import '../transcripts/TranscriptSelectionsOverview.css';
import './FocusGroupReportForLeadFacilitator.css';

interface SchoolWithFocusGroupStatus {
  id: string;
  name: string;
  status: string;
  dueDate: string;
  facilitators: Facilitator[];
}

const FocusGroupReportForLeadFacilitator = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState<SchoolWithFocusGroupStatus[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [finalizedReportUrls, setFinalizedReportUrls] = useState<Record<string, string>>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();

  const fetchSchoolsAndStatus = async (showLoader: boolean = true) => {
    if (!userDetails) return;

    try {
      if (showLoader) {
        setLoading(true);
      }

      const eoaConfigResponse = await getEOAConfig(userDetails.eoaId, userDetails.access_token);
      const sjwDate = eoaConfigResponse.data.sjw_date;

      const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      };

      const formattedDueDate = formatDate(sjwDate);

      const schoolsResponse = await getConfiguredSchools(userDetails.eoaId, userDetails.access_token);
      const configuredSchools = schoolsResponse.data;
      const primarySchools = configuredSchools.filter(
        (school: ConfiguredEOASchool) => school.is_primary_on_eoa !== false
      );

      const schoolsWithStatus = await Promise.all(
        primarySchools.map(async (school: ConfiguredEOASchool) => {
          try {
            const statusResponse = await getFocusGroupWorkflowStatus(
              userDetails.eoaId,
              school.id,
              userDetails.access_token
            );

            try {
              if (statusResponse.current_state === 'FINALIZED') {
                const reportResponse = await getFinalizedReportUrl({
                  eoaId: userDetails.eoaId,
                  access_token: userDetails.access_token,
                  schoolId: school.id,
                });

                if (reportResponse.download_url) {
                  setFinalizedReportUrls((prev) => ({
                    ...prev,
                    [school.id]: reportResponse.download_url,
                  }));
                }
              }
            } catch (error) {}

            let facilitators: Facilitator[] = [];
            try {
              const facilitatorsResponse = await getSchoolFacilitators({
                eoaId: userDetails.eoaId,
                access_token: userDetails.access_token,
                schoolId: school.id,
              });
              facilitators = facilitatorsResponse.facilitators || [];
            } catch (error) {
              facilitators = [];
            }

            return {
              id: school.id,
              name: school.name,
              status: statusResponse.current_state,
              dueDate: formattedDueDate,
              facilitators,
            };
          } catch (error) {
            // Try to fetch facilitators even if status fetch failed
            let facilitators: Facilitator[] = [];
            try {
              const facilitatorsResponse = await getSchoolFacilitators({
                eoaId: userDetails.eoaId,
                access_token: userDetails.access_token,
                schoolId: school.id,
              });
              facilitators = facilitatorsResponse.facilitators || [];
            } catch (error) {
              facilitators = [];
            }

            return {
              id: school.id,
              name: school.name,
              status: 'NOT_STARTED',
              dueDate: formattedDueDate,
              facilitators,
            };
          }
        })
      );

      setSchools(schoolsWithStatus);
    } catch (error) {
      // Error handling is managed by the UI state
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchSchoolsAndStatus();
  }, [userDetails]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && userDetails && selectedSchoolId) {
      setIsUploading(true);
      try {
        const urlResponse = await postRequestUploadUrl(userDetails.eoaId, userDetails.access_token, {
          file_name: file.name,
          user_provided_file_metadata: {
            school_id: selectedSchoolId,
            focus_group_datetime: new Date().toISOString(),
          },
          upload_file_type: fileTypes.FOCUS_GROUP_FINALIZED_REPORT,
        });

        await uploadFileToS3Bucket(urlResponse.data.presigned_url, file, (_progress) => {
          console.log('progress', _progress);
        });

        // Keep spinner and check for finalized report URL with retry logic
        const maxRetries = 5;
        const retryDelay = 2000;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            const reportResponse = await getFinalizedReportUrl({
              eoaId: userDetails.eoaId,
              access_token: userDetails.access_token,
              schoolId: selectedSchoolId,
            });

            if (reportResponse.download_url) {
              setFinalizedReportUrls((prev) => ({
                ...prev,
                [selectedSchoolId]: reportResponse.download_url,
              }));
              break;
            }
          } catch (error) {
            if (attempt < maxRetries - 1) {
              await new Promise((resolve) => setTimeout(resolve, retryDelay));
            }
          }
        }

        try {
          const statusResponse = await getFocusGroupWorkflowStatus(
            userDetails.eoaId,
            selectedSchoolId,
            userDetails.access_token
          );

          setSchools((prevSchools) =>
            prevSchools.map((school) =>
              school.id === selectedSchoolId
                ? {
                    ...school,
                    status: statusResponse.current_state,
                  }
                : school
            )
          );
        } catch (error) {}

        setShowSuccessMessage(true);
      } catch (error) {
        setShowErrorMessage(true);
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handleConfirmUpload = () => {
    setShowUploadModal(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRetryUpload = () => {
    setShowErrorMessage(false);
    setShowUploadModal(true);
  };

  if (loading) {
    return <Loader />;
  }

  if (!userDetails) {
    return <div>User details not available</div>;
  }

  const isLeadFacilitatorOrAdmin =
    userDetails.role === USER_ROLES.XQ_ADMIN || userDetails.role === USER_ROLES.LEAD_FACILITATOR;

  if (!isLeadFacilitatorOrAdmin) {
    return (
      <div className="main-content">
        <Panel>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h2>Access Denied</h2>
            <p>You don't have permission to access this page.</p>
          </div>
        </Panel>
      </div>
    );
  }

  const renderActionButton = (school: SchoolWithFocusGroupStatus) => {
    if (school.status === 'FINALIZED') {
      const finalizedReportUrl = finalizedReportUrls[school.id];
      const reportUrl = finalizedReportUrl || `/school-focus-group/${school.id}`;

      return (
        <a
          href={reportUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#006666',
            textDecoration: 'none',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <img src="/images/footer-new-window.png" alt="external link" width="16" height="16" />
          View report
        </a>
      );
    }

    if (school.status === 'AI_GENERATED') {
      const finalizedReportUrl = finalizedReportUrls[school.id];
      const reportUrl = finalizedReportUrl || `/school-focus-group/${school.id}`;

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <a
            href={reportUrl}
            onClick={(e) => {
              if (!finalizedReportUrl) {
                e.preventDefault();
                navigate(`/school-focus-group/${school.id}`);
              }
            }}
            style={{
              color: '#006666',
              textDecoration: 'none',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {finalizedReportUrl ? 'View final report' : 'View draft report'}
          </a>
          <CustomButton
            variant="secondary"
            size="small"
            buttonText="Upload final report"
            buttonAction={() => {
              setSelectedSchoolId(school.id);
              setShowUploadModal(true);
            }}
            loading={isUploading && selectedSchoolId === school.id}
            className="focus-group-upload-button"
            sx={{
              backgroundColor: isUploading && selectedSchoolId === school.id ? '#D3D3D3' : '#006666',
              color: 'white',
              borderRadius: isUploading && selectedSchoolId === school.id ? '50%' : '20px',
              padding: isUploading && selectedSchoolId === school.id ? '0' : '8px 16px',
              textTransform: 'none',
              width: isUploading && selectedSchoolId === school.id ? '48px' : 'fit-content',
              height: isUploading && selectedSchoolId === school.id ? '48px' : 'auto',
              minWidth: isUploading && selectedSchoolId === school.id ? '48px' : 'auto',
              '&:hover': {
                backgroundColor: isUploading && selectedSchoolId === school.id ? '#D3D3D3' : '#004d4d',
              },
              '&.Mui-disabled': {
                backgroundColor: isUploading && selectedSchoolId === school.id ? '#D3D3D3' : undefined,
                color: 'white',
              },
            }}
          />
        </div>
      );
    }

    if (
      school.status === 'NOT_STARTED' ||
      school.status === 'GENERATION_IN_PROGRESS' ||
      school.status === 'AI_FAILED'
    ) {
      return (
        <CustomButton
          variant="secondary"
          size="small"
          buttonText="Upload final report"
          buttonAction={() => {
            setSelectedSchoolId(school.id);
            setShowUploadModal(true);
          }}
          loading={isUploading && selectedSchoolId === school.id}
          className="focus-group-upload-button"
          sx={{
            backgroundColor: isUploading && selectedSchoolId === school.id ? '#D3D3D3' : '#006666',
            color: 'white',
            borderRadius: isUploading && selectedSchoolId === school.id ? '50%' : '20px',
            padding: isUploading && selectedSchoolId === school.id ? '0' : '8px 16px',
            textTransform: 'none',
            width: isUploading && selectedSchoolId === school.id ? '48px' : 'fit-content',
            height: isUploading && selectedSchoolId === school.id ? '48px' : 'auto',
            minWidth: isUploading && selectedSchoolId === school.id ? '48px' : 'auto',
            '&:hover': {
              backgroundColor: isUploading && selectedSchoolId === school.id ? '#D3D3D3' : '#004d4d',
            },
            '&.Mui-disabled': {
              backgroundColor: isUploading && selectedSchoolId === school.id ? '#D3D3D3' : undefined,
              color: 'white',
            },
          }}
        />
      );
    }

    return null;
  };

  const tableHeaderData: TableHeaderData<SchoolWithFocusGroupStatus>[] = [
    { key: 'name', label: 'School', icons: ['sort'] },
    {
      key: 'status',
      label: 'Status',
      icons: ['sort'],
      renderCell: (school) => <FocusGroupReportStatus status={school.status} size="small" />,
    },
    { key: 'dueDate', label: 'Due date' },
    {
      key: 'facilitators',
      label: 'Facilitators',
      renderCell: (school) => (
        <span style={{ color: '#333333' }}>
          {school.facilitators.length > 0
            ? school.facilitators.map((f) => f.name).join(', ')
            : 'No facilitators assigned'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      renderCell: (school) => renderActionButton(school),
    },
  ];

  return (
    <>
      <div className="main-content">
        <CustomButton
          variant="text"
          buttonText="Back"
          icon={<img src={GoBackIcon} alt="go back" />}
          iconPosition="start"
          buttonAction={() => navigate('/overview')}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 className="transcript-selections-page-title" style={{ margin: 0 }}>
            Focus group report
          </h1>
          <CustomButton
            variant="secondary"
            size="small"
            buttonText="Focus group tool"
            buttonAction={() => navigate('/school-focus-groups')}
            sx={{
              backgroundColor: '#006666',
              color: 'white',
              borderRadius: '20px',
              padding: '10px 20px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#004d4d',
              },
            }}
          />
        </div>
        <Panel className="transcript-selections-overview-panel">
          <h2 className="transcript-selections-panel-title">Focus group reports</h2>
          {loading && schools.length === 0 ? (
            <TableSkeleton rowsNumber={3} colsNumber={tableHeaderData.length} />
          ) : (
            <CustomTable tableHeaderData={tableHeaderData} tableBodyData={schools} />
          )}
        </Panel>
      </div>

      <CustomConfirmationModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onConfirm={handleConfirmUpload}
        title="Are you sure?"
        message={
          <>
            By confirming, you'll upload the <strong>final Focus Group report</strong>. Once it's uploaded, you won't be
            able to replace it.
          </>
        }
        confirmText="Confirm"
        cancelText="Cancel"
      />

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        aria-label="Upload file"
        accept=".pdf,.doc,.docx,.txt,.rtf"
        onChange={handleFileChange}
      />

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={4000}
        onClose={() => setShowSuccessMessage(false)}
        message="A file uploaded successfully"
      />

      <Snackbar
        open={showErrorMessage}
        autoHideDuration={10000}
        onClose={() => setShowErrorMessage(false)}
        message={
          <span>
            A file upload failed.{' '}
            <button
              onClick={handleRetryUpload}
              style={{
                color: 'rgb(161, 248, 171)',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                font: 'inherit',
                fontWeight: 'bold',
              }}
            >
              Try again
            </button>
          </span>
        }
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#363636',
            color: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            maxWidth: 'fit-content',
            width: 'auto',
            minWidth: 'auto',
          },
        }}
      />
    </>
  );
};

export default FocusGroupReportForLeadFacilitator;
